import React from 'react';

// Extracted from App.js — the 4-step itinerary planning wizard. All state and
// handlers are owned by the parent (CulinaraApp) and passed in as props.
export default function ItineraryWizard({ currentStep, totalSteps, formData, handleInputChange, handleCheckboxChange, nextStep, prevStep, goHome, submitForm, emailError }) {
  return (
      <div className="min-h-screen bg-gray-50">
        {/* Progress Bar */}
        <div className="bg-white border-b">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold text-gray-900">Plan Your Culinary Journey</h1>
              <span className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</span>
            </div>
            <div 
              className="w-full bg-gray-200 rounded-full h-2"
              role="progressbar"
              aria-valuenow={currentStep}
              aria-valuemin="1" 
              aria-valuemax={totalSteps}
              aria-label={`Step ${currentStep} of ${totalSteps} - Planning progress`}
            >
              <div 
                className="bg-teal-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                aria-hidden="true"
              ></div>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="max-w-2xl mx-auto px-4 py-8">
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Step 1: Trip Basics */}
            {currentStep === 1 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="mr-2">📍</span>
                  Trip Basics
                </h2>
                <p className="text-gray-600 mb-6">Let's start with the fundamentals of your culinary adventure</p>

                <p className="text-sm text-gray-500 mb-6">
                  Curious what you'll get?{" "}
                  <a href="/sample-itineraries" data-testid="wizard-sample-link" className="text-teal-600 font-semibold hover:underline">See what a finished trip looks like →</a>
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="startingLocation" className="block text-sm font-medium text-gray-700 mb-2">
                      Starting Location
                    </label>
                    <select 
                      id="startingLocation"
                      name="startingLocation"
                      data-testid="wizard-starting-location"
                      value={formData.startingLocation}
                      onChange={(e) => handleInputChange('startingLocation', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      required
                    >
                      <option value="">Select your starting city</option>
                      <option value="Calgary">Calgary</option>
                      <option value="Edmonton">Edmonton</option>
                      <option value="Banff">Banff</option>
                      <option value="Canmore">Canmore</option>
                      <option value="Jasper">Jasper</option>
                      <option value="Red Deer">Red Deer</option>
                      <option value="Lethbridge">Lethbridge</option>
                      <option value="Medicine Hat">Medicine Hat</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="tripDuration" className="block text-sm font-medium text-gray-700 mb-2">
                      Trip Duration
                    </label>
                    <select 
                      id="tripDuration"
                      name="tripDuration"
                      data-testid="wizard-trip-duration"
                      value={formData.tripDuration}
                      onChange={(e) => handleInputChange('tripDuration', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="3 Days">3 Days</option>
                      <option value="4 Days">4 Days</option>
                      <option value="5 Days">5 Days</option>
                      <option value="1 Week">1 Week</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="travelDates" className="block text-sm font-medium text-gray-700 mb-2">
                      Travel Dates
                    </label>
                    <input 
                      type="date"
                      id="travelDates"
                      name="travelDates"
                      data-testid="wizard-travel-dates"
                      value={formData.travelDates}
                      onChange={(e) => handleInputChange('travelDates', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="groupSize" className="block text-sm font-medium text-gray-700 mb-2">
                      Group Size
                    </label>
                    <select 
                      id="groupSize"
                      name="groupSize"
                      data-testid="wizard-group-size"
                      value={formData.groupSize}
                      onChange={(e) => handleInputChange('groupSize', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="1 Person">1 Person</option>
                      <option value="2 People">2 People</option>
                      <option value="3-4 People">3-4 People</option>
                      <option value="5+ People">5+ People</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700 mb-3">Budget Range per Person</legend>
                    <div className="flex flex-wrap gap-4">
                      {['Budget', 'Moderate', 'Premium', 'Luxury'].map((budget) => (
                        <label key={budget} htmlFor={`budget-${budget.toLowerCase()}`} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            id={`budget-${budget.toLowerCase()}`}
                            name="budgetRange"
                            data-testid={`wizard-budget-${budget.toLowerCase()}`}
                            value={budget}
                            checked={formData.budgetRange === budget}
                            onChange={(e) => handleInputChange('budgetRange', e.target.value)}
                            className="sr-only"
                          />
                          <span className={`px-4 py-2 rounded-lg border ${
                            formData.budgetRange === budget 
                              ? 'bg-teal-50 border-teal-500 text-teal-700' 
                              : 'bg-white border-gray-300 text-gray-700'
                          }`}>
                            {'$'.repeat(budget === 'Budget' ? 1 : budget === 'Moderate' ? 2 : budget === 'Premium' ? 3 : 4)}
                            <br />
                            <span className="text-xs">{budget}</span>
                          </span>
                          <span className="sr-only">{budget} budget option</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>
              </div>
            )}

            {/* Step 2: Culinary Preferences */}
            {currentStep === 2 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="mr-2">🍽️</span>
                  Culinary Preferences
                </h2>
                <p className="text-gray-600 mb-6">Tell us about your food preferences and dietary needs</p>

                <div className="mb-6">
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700 mb-3">
                      Cuisine Types (Select all that interest you)
                    </legend>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[
                        'Fine Dining', 'Steakhouses', 'Casual Dining',
                        'Contemporary', 'Canadian', 'International',
                        'Farm-to-Table', 'Japanese', 'Korean',
                        'Asian Fusion', 'Indigenous Cuisine', 'Seafood',
                        'Vegetarian/Vegan', 'Bakeries/Desserts', 'Breweries/Distilleries',
                        'Wine Bar', 'Food Trucks'
                      ].map((cuisine) => {
                        const cuisineId = `cuisine-${cuisine.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
                        return (
                          <label key={cuisine} htmlFor={cuisineId} className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                            <input
                              type="checkbox"
                              id={cuisineId}
                              name="cuisineTypes"
                              data-testid={`wizard-${cuisineId}`}
                              value={cuisine}
                              checked={formData.cuisineTypes.includes(cuisine)}
                              onChange={() => handleCheckboxChange('cuisineTypes', cuisine)}
                              className="mr-3 text-teal-600 focus:ring-teal-500 rounded"
                            />
                            <span className="text-sm text-gray-700">{cuisine}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                </div>

                <div className="mb-6">
                  <label htmlFor="dietaryRestrictions" className="block text-sm font-medium text-gray-700 mb-2">
                    Dietary Restrictions
                  </label>
                  <input 
                    type="text"
                    id="dietaryRestrictions"
                    name="dietaryRestrictions"
                    data-testid="wizard-dietary"
                    value={formData.dietaryRestrictions}
                    onChange={(e) => handleInputChange('dietaryRestrictions', e.target.value)}
                    placeholder="e.g., Vegetarian, Gluten-free, Allergies..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>

                <div>
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700 mb-3">Experience Level</legend>
                    <div className="flex flex-wrap gap-4">
                      {['Conservative', 'Moderate', 'Adventurous'].map((level) => (
                        <label key={level} htmlFor={`experience-${level.toLowerCase()}`} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            id={`experience-${level.toLowerCase()}`}
                            name="experienceLevel"
                            data-testid={`wizard-experience-${level.toLowerCase()}`}
                            value={level}
                            checked={formData.experienceLevel === level}
                            onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                            className="sr-only"
                          />
                          <span className={`px-4 py-2 rounded-lg border ${
                            formData.experienceLevel === level 
                              ? 'bg-teal-50 border-teal-500 text-teal-700' 
                              : 'bg-white border-gray-300 text-gray-700'
                          }`}>
                            {level}
                          </span>
                          <span className="sr-only">{level} experience level</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>
              </div>
            )}

            {/* Step 3: Travel Details */}
            {currentStep === 3 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="mr-2">🚗</span>
                  Travel Details
                </h2>
                <p className="text-gray-600 mb-6">Help us plan your journey logistics</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="accommodationType" className="block text-sm font-medium text-gray-700 mb-2">
                      Accommodation Type
                    </label>
                    <select 
                      id="accommodationType"
                      name="accommodationType"
                      data-testid="wizard-accommodation"
                      value={formData.accommodationType}
                      onChange={(e) => handleInputChange('accommodationType', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Select accommodation preference</option>
                      <option value="Hotels">Hotels</option>
                      <option value="B&B">Bed & Breakfast</option>
                      <option value="Boutique Hotels">Boutique Hotels</option>
                      <option value="Farm Stays">Farm Stays</option>
                      <option value="Camping">Camping</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="transportationMode" className="block text-sm font-medium text-gray-700 mb-2">
                      Transportation Mode
                    </label>
                    <select 
                      id="transportationMode"
                      name="transportationMode"
                      value={formData.transportationMode}
                      onChange={(e) => handleInputChange('transportationMode', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Select transportation</option>
                      <option value="Own Vehicle">Own Vehicle</option>
                      <option value="Rental Car">Rental Car</option>
                      <option value="RV/Motorhome">RV/Motorhome</option>
                      <option value="Guided Tour">Guided Tour</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-2">
                    Special Requests or Interests
                  </label>
                  <textarea 
                    id="specialRequests"
                    name="specialRequests"
                    data-testid="wizard-special-requests"
                    value={formData.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                    placeholder="Any specific restaurants, experiences, or requirements you'd like us to consider..."
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Contact Information */}
            {currentStep === 4 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="mr-2">📧</span>
                  Contact Information
                </h2>
                <p className="text-gray-600 mb-6">We'll send your personalized itinerary to this email</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input 
                      type="email"
                      id="contactEmail"
                      name="contactEmail"
                      data-testid="wizard-contact-email"
                      value={formData.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      placeholder="your.email@example.com"
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? 'contactEmail-error' : undefined}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${emailError ? 'border-red-500' : 'border-gray-300'}`}
                      required
                    />
                    {emailError && (
                      <p id="contactEmail-error" data-testid="wizard-email-error" role="alert" className="mt-2 text-sm text-red-600">{emailError}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number (Optional)
                    </label>
                    <input 
                      type="tel"
                      id="contactPhone"
                      name="contactPhone"
                      data-testid="wizard-contact-phone"
                      value={formData.contactPhone}
                      onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                      placeholder="(403) 123-4567"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>

                <div className="mt-6 p-4 bg-teal-50 rounded-lg">
                  <h3 className="font-semibold text-teal-800 mb-2">Your Culinary Journey Summary</h3>
                  <div className="text-sm text-teal-700 space-y-1">
                    <p><strong>Location:</strong> {formData.startingLocation || 'Not selected'}</p>
                    <p><strong>Duration:</strong> {formData.tripDuration}</p>
                    <p><strong>Group Size:</strong> {formData.groupSize}</p>
                    <p><strong>Budget:</strong> {formData.budgetRange}</p>
                    <p><strong>Cuisines:</strong> {formData.cuisineTypes.length > 0 ? formData.cuisineTypes.join(', ') : 'None selected'}</p>
                    <p><strong>Experience Level:</strong> {formData.experienceLevel}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                type="button"
                data-testid="wizard-prev-btn"
                onClick={currentStep === 1 ? goHome : prevStep}
                className="flex items-center px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
              >
                ← {currentStep === 1 ? 'Back to Home' : 'Previous'}
              </button>

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  data-testid="wizard-next-btn"
                  onClick={nextStep}
                  className="flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Next →
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="wizard-submit-btn"
                  onClick={submitForm}
                  className="flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Send My Itinerary →
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
  );
}
